// Integracoes por empresa (Sessao 46): conexao com o ERP, rotinas (catalogo) e execucoes. Nada aqui guarda ou devolve credencial.
import { z } from 'zod';
import { cuid, taxa } from './primitives.js';
import { classificacaoProduto, itemTipo } from './itens.js';
import type { ImpactoCascataResumo } from './impacto-cascata.js';

export const SISTEMAS_INTEGRACAO = ['PROTHEUS', 'ORBIT'] as const;
export type SistemaIntegracao = (typeof SISTEMAS_INTEGRACAO)[number];

export const DIRECOES_ROTINA = ['IMPORTAR', 'EXPORTAR', 'DESLIGADA'] as const;
export type DirecaoRotina = (typeof DIRECOES_ROTINA)[number];

export const FREQUENCIAS_ROTINA = ['MANUAL', 'DIARIA'] as const;
export type FrequenciaRotina = (typeof FREQUENCIAS_ROTINA)[number];

export const AUTENTICACOES_PROTHEUS = ['BASIC', 'OAUTH2'] as const;
export type AutenticacaoProtheus = (typeof AUTENTICACOES_PROTHEUS)[number];

/** Entidade do XARP que a rotina alimenta (tambem e a `entidade` do ExternalRef). */
export type EntidadeRotina = 'Unidade' | 'CondicaoPagamento' | 'Cliente' | 'Fornecedor' | 'Item' | 'ItemTributo' | 'FichaTecnica';

export interface RotinaCatalogo {
  codigo: string;
  sistema: SistemaIntegracao;
  rotulo: string;
  /** Chave do modulo (agrupa na tela). */
  modulo: string;
  moduloRotulo: string;
  /** Direcoes que o adaptador sabe executar (DESLIGADA e sempre possivel). Direcao fora da lista nao e aceita. */
  direcoes: readonly Exclude<DirecaoRotina, 'DESLIGADA'>[];
  entidade: EntidadeRotina;
  /** Tabela do ERP de origem (so informativo). */
  origem: string;
  /** Rotinas que convem rodar antes (so informativo). */
  dependencias: readonly string[];
}

/** Catalogo das rotinas: e codigo, nao tabela. A tela lista isto e cria a linha de `RotinaIntegracao` quando o usuario liga a rotina. */
export const CATALOGO_ROTINAS: readonly RotinaCatalogo[] = [
  { codigo: 'protheus.unidades', sistema: 'PROTHEUS', rotulo: 'Unidades de medida', modulo: 'cadastros', moduloRotulo: 'Cadastros', direcoes: ['IMPORTAR'], entidade: 'Unidade', origem: 'SAH', dependencias: [] },
  { codigo: 'protheus.condicoes', sistema: 'PROTHEUS', rotulo: 'Condições de pagamento', modulo: 'cadastros', moduloRotulo: 'Cadastros', direcoes: ['IMPORTAR'], entidade: 'CondicaoPagamento', origem: 'SE4', dependencias: [] },
  { codigo: 'protheus.clientes', sistema: 'PROTHEUS', rotulo: 'Clientes', modulo: 'cadastros', moduloRotulo: 'Cadastros', direcoes: ['IMPORTAR'], entidade: 'Cliente', origem: 'SA1', dependencias: [] },
  { codigo: 'protheus.fornecedores', sistema: 'PROTHEUS', rotulo: 'Fornecedores', modulo: 'cadastros', moduloRotulo: 'Cadastros', direcoes: ['IMPORTAR'], entidade: 'Fornecedor', origem: 'SA2', dependencias: [] },
  { codigo: 'protheus.produtos', sistema: 'PROTHEUS', rotulo: 'Produtos', modulo: 'produtos', moduloRotulo: 'Produtos e estrutura', direcoes: ['IMPORTAR'], entidade: 'Item', origem: 'SB1', dependencias: ['protheus.unidades'] },
  { codigo: 'protheus.tributacao', sistema: 'PROTHEUS', rotulo: 'Tributação dos produtos', modulo: 'produtos', moduloRotulo: 'Produtos e estrutura', direcoes: ['IMPORTAR'], entidade: 'ItemTributo', origem: 'SB1 + grupo tributário', dependencias: ['protheus.produtos'] },
  { codigo: 'protheus.estrutura', sistema: 'PROTHEUS', rotulo: 'Estrutura (ficha técnica)', modulo: 'produtos', moduloRotulo: 'Produtos e estrutura', direcoes: ['IMPORTAR'], entidade: 'FichaTecnica', origem: 'SG1', dependencias: ['protheus.produtos'] },
  { codigo: 'protheus.roteiro', sistema: 'PROTHEUS', rotulo: 'Roteiro (mão de obra)', modulo: 'produtos', moduloRotulo: 'Produtos e estrutura', direcoes: ['IMPORTAR'], entidade: 'FichaTecnica', origem: 'SG2 + SH1', dependencias: ['protheus.estrutura'] },
];

/**
 * Campos que vem do Protheus por entidade (somente leitura na tela quando `doProtheus`). Nomes dos campos do formulario/DTO de cada cadastro;
 * `enderecoPrincipal` e `contatoProtheus` sao o endereco principal e o contato "Protheus" (e-mail/telefone) do parceiro.
 */
export const CAMPOS_DO_PROTHEUS: Readonly<Record<EntidadeRotina, readonly string[]>> = {
  Unidade: ['codigo', 'descricao'],
  CondicaoPagamento: ['nome', 'ativa', 'parcelas'],
  Cliente: ['nome', 'documento', 'natureza', 'fantasia', 'ie', 'contribuinteIcms', 'enderecoPrincipal', 'contatoProtheus'],
  Fornecedor: ['nome', 'documento', 'natureza', 'fantasia', 'ie', 'contribuinteIcms', 'ativo', 'enderecoPrincipal', 'contatoProtheus'],
  Item: ['codigo', 'descricao', 'unidade', 'ativo', 'tipo'],
  ItemTributo: ['tributos'],
  FichaTecnica: ['componentes', 'nova-versao', 'escolhida'],
};

export function rotinaDoCatalogo(sistema: SistemaIntegracao, codigo: string): RotinaCatalogo | undefined {
  return CATALOGO_ROTINAS.find((r) => r.sistema === sistema && r.codigo === codigo);
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Entrada
// ---------------------------------------------------------------------------------------------------------------------------------

const urlHttp = z
  .string()
  .trim()
  .max(300, 'A URL tem no máximo 300 caracteres')
  .refine((v) => {
    try {
      const u = new URL(v);
      return (u.protocol === 'http:' || u.protocol === 'https:') && !u.username && !u.password && !u.search && !u.hash;
    } catch {
      return false;
    }
  }, 'Informe uma URL http(s) sem usuário, parâmetros nem âncora (ex.: https://servidor:8080/rest)');

/** POST/PUT da conexao. `credencial` vazia ou ausente mantem a atual (na criacao e obrigatoria). */
export const salvarConexaoSchema = z.object({
  nome: z.string().trim().min(2, 'Informe o nome (mínimo 2 caracteres)').max(80),
  baseUrl: urlHttp,
  /** "empresa,filial" do Protheus (header tenantId). */
  tenantProtheus: z.string().trim().regex(/^[A-Za-z0-9]{1,12},[A-Za-z0-9]{1,12}$/, 'Use o formato empresa,filial (ex.: 01,01)'),
  autenticacao: z.enum(AUTENTICACOES_PROTHEUS),
  usuario: z.string().trim().min(1, 'Informe o usuário').max(120),
  credencial: z.string().max(500).optional(),
  pageSize: z.number().int().min(1).max(500).default(100),
  timeoutMs: z.number().int().min(1000).max(120_000).default(15_000),
  ensaio: z.boolean(),
  ativa: z.boolean(),
});
export type SalvarConexaoDto = z.output<typeof salvarConexaoSchema>;

/** Caminho REST de uma rotina (relativo a URL base); vazio = o padrao do catalogo. */
const caminhoRest = z.string().trim().regex(/^\/[A-Za-z0-9_\-./]{1,120}$/, 'Caminho inválido (ex.: /api/pcp/v1/mrpbillofmaterial)');

/**
 * Escopo da rotina (filtros e opcoes). As opcoes de estrutura/roteiro so valem para essas rotinas; todas tem padrao, entao o objeto vem
 * sempre completo. `caminho` troca o endpoint do catalogo (as APIs TOTVS variam de versao entre instalacoes).
 */
export const escopoRotinaSchema = z.object({
  incluirBloqueados: z.boolean().default(false),
  caminho: caminhoRest.optional(),
  /** Estrutura: de onde vem a mao de obra da ficha (uma so origem, para nao contar em dobro). */
  origemMaoDeObra: z.enum(['MOD_NA_ESTRUTURA', 'ROTEIRO']).default('MOD_NA_ESTRUTURA'),
  /** Estrutura: componente cujo codigo comeca assim e mao de obra direta (MOD). */
  prefixoMod: z.string().trim().min(1).max(20).default('MOD'),
  /** Estrutura: unidade da quantidade da MOD na origem (vira minutos). */
  unidadeMod: z.enum(['HORAS', 'MINUTOS']).default('HORAS'),
  /** Estrutura: GGF (gastos gerais) ignorado por padrao (o CIF vem do rateio; somar daria contagem em dobro). */
  ggf: z.enum(['IGNORAR', 'COMO_MATERIAL']).default('IGNORAR'),
  /** Estrutura: G1_PERDA e percentual sobre a ENTRADA (padrao Protheus: q / (1 - p)) ou sobre a SAIDA (q x (1 + p)). */
  perdaSemantica: z.enum(['SOBRE_ENTRADA', 'SOBRE_SAIDA']).default('SOBRE_ENTRADA'),
  /** Roteiro: codigo do roteiro padrao (vazio = o do produto, B1_OPERPAD, ou "01"). */
  roteiroPadrao: z.string().trim().max(10).optional(),
  /** Roteiro: unidade dos tempos (tempo padrao e setup) na origem. */
  unidadeTempo: z.enum(['HORAS', 'MINUTOS']).default('HORAS'),
});
export type EscopoRotina = z.output<typeof escopoRotinaSchema>;

const horaDiaria = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm (ex.: 02:30)');

/** PUT da rotina (liga, desliga, frequencia, escopo). */
export const salvarRotinaSchema = z
  .object({
    direcao: z.enum(DIRECOES_ROTINA),
    frequencia: z.enum(FREQUENCIAS_ROTINA).default('MANUAL'),
    horaDiaria: horaDiaria.nullable().default(null),
    escopo: escopoRotinaSchema.default(() => escopoRotinaSchema.parse({})),
  })
  .refine((d) => d.frequencia !== 'DIARIA' || d.horaDiaria !== null, { path: ['horaDiaria'], message: 'Informe a hora da execução diária' });
export type SalvarRotinaDto = z.output<typeof salvarRotinaSchema>;

export const codigoRotinaParam = z.object({ codigo: z.string().regex(/^[a-z]+\.[a-z_]+$/, 'Código de rotina inválido') });
export const execucaoIdParam = z.object({ id: cuid });

// ---------------------------------------------------------------------------------------------------------------------------------
// Saida
// ---------------------------------------------------------------------------------------------------------------------------------

export interface TesteConexaoResultado {
  ok: boolean;
  em: string;
  latenciaMs: number | null;
  /** Status HTTP da ultima chamada (quando houve resposta). */
  status: number | null;
  versao: string | null;
  /** Codigo estavel do erro traduzido (DNS, TLS, 401, 403, 404, TIMEOUT, ...). */
  erroCodigo: string | null;
  erro: string | null;
  /** Rotina cujo endpoint foi chamado. */
  rotina: string | null;
}

export interface ConexaoResponse {
  id: string;
  sistema: SistemaIntegracao;
  nome: string;
  baseUrl: string;
  tenantProtheus: string;
  autenticacao: AutenticacaoProtheus;
  usuario: string;
  /** A credencial nunca volta: so se existe. */
  credencialDefinida: boolean;
  pageSize: number;
  timeoutMs: number;
  ensaio: boolean;
  ativa: boolean;
  ultimoTesteEm: string | null;
  ultimoTesteResultado: TesteConexaoResultado | null;
}

export const EXECUCAO_STATUS = ['EXECUTANDO', 'CONCLUIDA', 'FALHOU'] as const;
export type ExecucaoStatus = (typeof EXECUCAO_STATUS)[number];

export interface RegistroRejeitado {
  /** Chave do registro no ERP (sem prefixo interno). */
  idExterno: string;
  motivo: string;
  /** Campos envolvidos (nomes, nunca valores pessoais alem do necessario para corrigir). */
  campos: string[];
}

export interface RelatorioExecucao {
  lidos: number;
  criados: number;
  atualizados: number;
  iguais: number;
  inativados: number;
  /** Casaram com um cadastro que ja existia no XARP (CNPJ, sigla ou codigo iguais) e foram vinculados em vez de duplicados; ja contam em `atualizados`. */
  vinculados: number;
  /** Bloqueados na origem que nunca foram trazidos (rotina sem "incluir bloqueados"). */
  ignorados: number;
  rejeitados: number;
  duracaoMs: number;
  /** Lista dos rejeitados (limitada a RELATORIO_MAX_REJEITADOS; o contador `rejeitados` e o total). */
  rejeitadosLista: RegistroRejeitado[];
  /** Produtos: contagem por tipo do XARP que o ensaio/execucao resultou (ex.: { PRODUTO: 12, MATERIA_PRIMA: 40 }). */
  porTipo?: Record<string, number>;
  /** Valores da origem sem linha no de-para (tipo de produto, grupo tributario, codigo MOD, recurso), com a quantidade de registros. */
  semDePara?: { tipo: TipoDePara; chave: string; quantidade: number }[];
  /** Registros ignorados por regra (ex.: tipo MO/GG mapeado para IGNORAR). */
  ignoradosLista?: { idExterno: string; motivo: string }[];
  /** Avisos que nao rejeitam (ex.: GGF ignorado na estrutura, grupo sem categoria no XARP). */
  avisos?: string[];
  /** Diagnostico de conexao (rotina `diagnostico`). */
  diagnostico?: DiagnosticoResultado;
  /** Impacto em cascata da execucao (Sessao 49, F): fichas escolhidas que mudam de custo e itens em "revisar preco". Ausente quando a rotina nao mede. */
  impacto?: ImpactoCascataResumo;
  /** Falha de transporte/autenticacao/interrupcao: mensagem legivel (so quando status = FALHOU). */
  erro?: string;
}

export const RELATORIO_MAX_REJEITADOS = 200;

// ---------------------------------------------------------------------------------------------------------------------------------
// De-paras por empresa (Sessao 47)
// ---------------------------------------------------------------------------------------------------------------------------------

export const TIPOS_DEPARA = ['TIPO_PRODUTO', 'GRUPO_TRIBUTARIO', 'MOD_CENTRO', 'RECURSO_CENTRO'] as const;
export type TipoDePara = (typeof TIPOS_DEPARA)[number];

export const ROTULO_DEPARA: Record<TipoDePara, string> = {
  TIPO_PRODUTO: 'Tipo de produto (B1_TIPO) → tipo do item',
  GRUPO_TRIBUTARIO: 'Grupo tributário (B1_GRTRIB) → alíquotas e CSTs',
  MOD_CENTRO: 'Código MOD → centro de custo',
  RECURSO_CENTRO: 'Recurso (G2_RECURSO) → centro de custo',
};

/** Rotina cujo de-para e usado (a tela mostra a tabela no painel dela). */
export const ROTINA_DO_DEPARA: Record<TipoDePara, string> = {
  TIPO_PRODUTO: 'protheus.produtos',
  GRUPO_TRIBUTARIO: 'protheus.tributacao',
  MOD_CENTRO: 'protheus.estrutura',
  RECURSO_CENTRO: 'protheus.roteiro',
};

export const DESTINO_TIPO_IGNORAR = 'IGNORAR' as const;
const destinoTipo = z.union([itemTipo, z.literal(DESTINO_TIPO_IGNORAR)]);

/** Padrao do de-para de tipos (B1_TIPO): MO e GG nao sao itens no XARP (mao de obra e GGF) e ficam em IGNORAR. */
export const TIPO_PRODUTO_PADRAO: Readonly<Record<string, { destino: z.infer<typeof destinoTipo>; classificacao?: z.infer<typeof classificacaoProduto> }>> = {
  PA: { destino: 'PRODUTO', classificacao: 'ACABADO' },
  PI: { destino: 'PRODUTO', classificacao: 'INTERMEDIARIO' },
  MP: { destino: 'MATERIA_PRIMA' },
  EM: { destino: 'EMBALAGEM' },
  ME: { destino: 'MERCADORIA' },
  MC: { destino: 'MATERIAL_AUXILIAR' },
  BN: { destino: 'SERVICO_TERCEIRO' },
  SV: { destino: 'SERVICO' },
  MO: { destino: 'IGNORAR' },
  GG: { destino: 'IGNORAR' },
};

export const valorTipoProdutoSchema = z.object({ destino: destinoTipo, classificacao: classificacaoProduto.optional() }).strict();
const cst2 = z.string().regex(/^\d{2}$/, 'CST com 2 dígitos');
export const valorGrupoTributarioSchema = z
  .object({
    icms: taxa.default('0'),
    pis: taxa.default('0'),
    cofins: taxa.default('0'),
    iss: taxa.default('0'),
    cstIcms: z.string().regex(/^\d{2,3}$/, 'CST/CSOSN com 2 ou 3 dígitos').optional(),
    cstPis: cst2.optional(),
    cstCofins: cst2.optional(),
    cstIpi: cst2.optional(),
    icmsSt: z.boolean().optional(),
    cstIbsCbs: z.string().regex(/^\d{3}$/, 'CST IBS/CBS com 3 dígitos').optional(),
    cClassTrib: z.string().regex(/^\d{6}$/, 'cClassTrib com 6 dígitos').optional(),
  })
  .strict();
export const valorCentroSchema = z.object({ centroCustoId: cuid }).strict();

export const VALOR_DEPARA_SCHEMA = {
  TIPO_PRODUTO: valorTipoProdutoSchema,
  GRUPO_TRIBUTARIO: valorGrupoTributarioSchema,
  MOD_CENTRO: valorCentroSchema,
  RECURSO_CENTRO: valorCentroSchema,
} as const;

export const tipoDeParaParam = z.object({ tipo: z.enum(TIPOS_DEPARA) });

/** PUT /integracoes/protheus/depara/:tipo - `valor` nulo = destino vazio (a linha continua, sem destino). */
export const salvarDeParaSchema = z.object({
  linhas: z
    .array(z.object({ chave: z.string().trim().min(1).max(40), valor: z.record(z.string(), z.unknown()).nullable() }))
    .min(1)
    .max(500),
});
export type SalvarDeParaDto = z.infer<typeof salvarDeParaSchema>;

export interface DeParaLinha {
  chave: string;
  valor: Record<string, unknown> | null;
  atualizadoEm: string;
}
export interface DeParaResponse {
  tipo: TipoDePara;
  linhas: DeParaLinha[];
  /** Ultima alteracao desta tabela (a trava de ensaio dos produtos compara com ela). */
  alteradoEm: string | null;
}
/** POST /integracoes/protheus/depara/:tipo/descobrir: codigos distintos vistos no Protheus (ja incluidos na tabela, com destino vazio). */
export interface DeParaDescobertaResponse {
  tipo: TipoDePara;
  novas: string[];
  jaExistentes: number;
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Diagnostico de conexao (Sessao 47)
// ---------------------------------------------------------------------------------------------------------------------------------

export const CODIGO_DIAGNOSTICO = 'diagnostico';

export type EstadoCampo = 'preenchido' | 'vazio' | 'ausente';
export interface CoberturaCampo {
  campo: string;
  /** Qual chave do payload casou (a primeira candidata presente). */
  chave: string | null;
  estado: EstadoCampo;
}
export interface DiagnosticoRotina {
  rotina: string;
  rotulo: string;
  caminho: string;
  /** Endpoint existe neste servidor: 200 = sim; 404/405 = nao; outros = erro. */
  existe: boolean;
  status: number | null;
  ms: number | null;
  itens: number;
  erro: string | null;
  cobertura: CoberturaCampo[];
  /** Chaves que vieram nos registros e o de-para nao usa. */
  naoUsados: string[];
  /** Registros de amostra (ate 3) com CPF/CNPJ, IE, e-mail, telefone, nome de pessoa fisica e endereco MASCARADOS antes de persistir. */
  amostra: unknown[];
}
export interface DiagnosticoResultado {
  geradoEm: string;
  rotinas: DiagnosticoRotina[];
}

export interface ExecucaoResponse {
  id: string;
  rotina: string;
  status: ExecucaoStatus;
  ensaio: boolean;
  origem: 'MANUAL' | 'AGENDADA';
  iniciadaEm: string;
  terminadaEm: string | null;
  relatorio: RelatorioExecucao | null;
}

export interface RotinaLinha {
  codigo: string;
  rotulo: string;
  modulo: string;
  moduloRotulo: string;
  entidade: EntidadeRotina;
  origem: string;
  dependencias: string[];
  /** Direcoes que o adaptador suporta (a tela desabilita as demais). */
  direcoesSuportadas: Exclude<DirecaoRotina, 'DESLIGADA'>[];
  direcao: DirecaoRotina;
  frequencia: FrequenciaRotina;
  horaDiaria: string | null;
  escopo: EscopoRotina;
  ultimaExecucao: ExecucaoResponse | null;
  /** Dependencias que ainda nao estao em IMPORTAR (a rotina so liga com todas ligadas). */
  dependenciasPendentes: string[];
  /** O ultimo diagnostico viu o endpoint inexistente neste Protheus (motivo); nulo = sem indicio. */
  indisponivel: string | null;
}

/** GET /integracoes/protheus */
export interface IntegracaoProtheusResponse {
  /** false = INTEGRACOES_CHAVE_MESTRA ausente/invalida neste servidor (tudo desativado, falha fechada). */
  habilitado: boolean;
  mensagem: string | null;
  conexao: ConexaoResponse | null;
  rotinas: RotinaLinha[];
  /** Ultimo diagnostico concluido (a tela oferece baixar o JSON). */
  ultimoDiagnostico: ExecucaoResponse | null;
}

export const MSG_INTEGRACOES_DESATIVADAS = 'Integrações desativadas neste servidor';

// ---------- Catalogo de Integracoes (Sessao 48): resumo dos cards e logs de todas as integracoes ----------

export const STATUS_CARD_PROTHEUS = ['DESATIVADO_SERVIDOR', 'NAO_CONFIGURADO', 'EM_ENSAIO', 'ATIVO', 'COM_ERRO'] as const;
export type StatusCardProtheus = (typeof STATUS_CARD_PROTHEUS)[number];

/** GET /integracoes/resumo (`integracoes.gerenciar`): o que o catalogo de cards precisa, sem rotinas nem execucoes completas. */
export interface ResumoIntegracoesResponse {
  protheus: {
    status: StatusCardProtheus;
    rotinasLigadas: number;
    ultimaExecucao: { em: string; status: ExecucaoStatus } | null;
  };
  api: { tokensAtivos: number };
}

export const TAMANHO_PAGINA_LOGS = 25;

/** GET /integracoes/logs: filtros opcionais; `de`/`ate` sao datas civis (AAAA-MM-DD, inclusivas, fuso de Sao Paulo). */
export const logsIntegracoesQuerySchema = z.object({
  integracao: z.enum(SISTEMAS_INTEGRACAO).optional(),
  status: z.enum(['EXECUTANDO', 'CONCLUIDA', 'FALHOU']).optional(),
  de: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  ate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  pagina: z.coerce.number().int().min(1).max(100_000).default(1),
});
export type LogsIntegracoesQuery = z.infer<typeof logsIntegracoesQuerySchema>;

export interface LogExecucaoLinha {
  id: string;
  integracao: SistemaIntegracao;
  rotina: string;
  rotinaRotulo: string;
  ensaio: boolean;
  status: ExecucaoStatus;
  iniciadaEm: string;
  terminadaEm: string | null;
  lidos: number;
  criados: number;
  atualizados: number;
  rejeitados: number;
  /** Nome de quem executou; `null` com `agendada` = agendador. */
  executadaPorNome: string | null;
  agendada: boolean;
}

export interface LogsIntegracoesResponse {
  itens: LogExecucaoLinha[];
  total: number;
  pagina: number;
  tamanho: number;
}
